import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  Grid,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography
} from "@mui/material";
import {FirstPage, LastPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET, POST} from "APIclient";
import {useEffect, useState} from "react";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import {Dialog, DialogTitle, DialogContent, DialogActions} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {Snackbar, Alert} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import {Replay as ReplayIcon} from "@mui/icons-material";
import properties from "constants/ApplicationConstants";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <MDTypography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </MDTypography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <MDTypography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </MDTypography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 20, headerAlign: "left", align: "left"},
  {
    field: "empName",
    headerName: "Employee Name",
    width: 150,
    headerAlign: "left",
    align: "left"
  },
  {field: "empId", headerName: "EMP ID", width: 70, headerAlign: "left", align: "left"},
  {
    field: "joinDate",
    headerName: "Joining Date",
    width: 130,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "applyDate",
    headerName: "Applied Date",
    width: 130,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "endDate",
    headerName: "Relieving Date",
    width: 130,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "proRatedUpl",
    headerName: "UPL (Pro Rata)",
    width: 120,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const proRated = params.row.proRatedUpl || 0;
      const remaining = params.row.remainingUplBalance || 0;

      return (
        <span style={{fontWeight: 500}}>
          {remaining} <span style={{fontWeight: 400}}>({proRated})</span>
        </span>
      );
    }
  },
  {
    field: "finalEncashableUpl",
    headerName: "Encashed (Utilised)",
    width: 160,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const finalEncashableUpl = params.row.finalEncashableUpl || 0;
      const uplUtilizedInResignationYear = params.row.uplUtilizedInResignationYear || 0;

      return (
        <span style={{fontWeight: 500}}>
          {finalEncashableUpl}{" "}
          <span style={{fontWeight: 400}}>({uplUtilizedInResignationYear})</span>
        </span>
      );
    }
  },
  {
    field: "resignationNotice",
    headerName: "Notice Period",
    width: 145,
    headerAlign: "left",
    align: "left"
  },
  {
    field: "status",
    headerName: "Status",
    width: 90,
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        case "cancelled":
          buttonColor = "secondary";
          statusText = "Cancelled";
          break;
        case "revoke granted":
          buttonColor = "warning";
          statusText = "Revoke Granted";
          break;
        case "revoked":
          buttonColor = "error";
          statusText = "Revoked";
          break;
        default:
          buttonColor = "secondary";
          statusText = "Unknown";
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 180,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const status = params.row.status.toLowerCase();
      const isRejected = status === "rejected" || status === "revoked";
      const isPending = status === "pending";

      const disableActions = isRejected;
      return [
        <Tooltip title="Approve Resignation" arrow key={`approve-${params.id}`}>
          <GridActionsCellItem
            icon={<DoneIcon />}
            label="Approve"
            color="success"
            onClick={() => params.row.handleApprove(params.row)}
            disabled={disableActions || !isPending}
          />
        </Tooltip>,
        <Tooltip title="Reject Resignation" arrow key={`reject-${params.id}`}>
          <GridActionsCellItem
            icon={<ClearIcon />}
            label="Reject"
            color="error"
            onClick={() => params.row.handleDenied(params.row)}
            disabled={disableActions || !isPending}
          />
        </Tooltip>,
        <Tooltip title="Edit Notice Period" arrow key={`edit-${params.id}`}>
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            color="primary"
            onClick={() => params.row.handleEdit(params.row)}
            disabled={disableActions}
          />
        </Tooltip>,
        <Tooltip title="Revoke Permission" arrow key={`revoke-${params.id}`}>
          <GridActionsCellItem
            icon={<ReplayIcon />}
            label="Revoke"
            color="primary"
            onClick={() => params.row.handleRevokeResignation(params.row)}
            disabled={disableActions}
          />
        </Tooltip>,
        <Tooltip title="View Details" arrow key={`view-${params.id}`}>
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            color="primary"
            onClick={() => params.row.handleView(params.row)}
          />
        </Tooltip>
      ];
    }
  }
];

function ResignationRequestList() {
  const empId = Number(window.sessionStorage.getItem("empId"));
  const theme = useTheme();
  const [resignationRequests, setResignationRequests] = useState([]);
  const [filteredResignationRequests, setFilteredResignationRequests] = useState([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [empIdFilter, setEmpIdFilter] = useState("");
  const [empStatusFilter, setEmpStatusFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedEmpIdFilter, setAppliedEmpIdFilter] = useState("");
  const [appliedEmpStatusFilter, setAppliedEmpStatusFilter] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [selectedResignationRequest, setSelectedResignationRequest] = useState(null);
  const [openApprovalModal, setOpenApprovalModal] = useState(false);
  const [reason, setReason] = useState("");
  const [action, setAction] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState("success");
  const [snackMessage, setSnackMessage] = useState("");
  const handleClose = () => setSnackOpen(false);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [changedBy, setChangedBy] = useState("HR");
  const [changeNoticePeriodReason, setChangeNoticePeriodReason] = useState("");
  const [endDateError, setEndDateError] = useState("");

  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [revokeDate, setRevokeDate] = useState();

  const [changeNoticePeriodReasonError, setChangeNoticePeriodReasonError] = useState("");
  const [isReasonProvided, setIsReasonProvided] = useState(false);

  const handleEdit = row => {
    setSelectedResignationRequest(row);
    const displayDate = row.endDate;
    const parsedDate = dayjs(displayDate, "DD MMM YYYY");
    setEndDate(parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "");
    setChangeNoticePeriodReason(row.changeNoticePeriodReason || "");
    // setChangedBy(row.changedBy ? row.changedBy : "Admin");
    setOpenEditModal(true);
  };

  const handleViewDetails = resignationRequest => {
    setSelectedResignationRequest(resignationRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedResignationRequest(null);
  };

  const handleApprove = row => {
    setSelectedResignationRequest(row);
    setReason("");
    setAction("approve");
    setOpenApprovalModal(true);
  };

  const handleDenied = row => {
    setSelectedResignationRequest(row);
    setReason("");
    setAction("reject");
    setOpenApprovalModal(true);
  };

  const handleRevokeResignation = row => {
    setSelectedResignationRequest(row);
    setRevokeDate(dayjs().format("YYYY-MM-DD"));
    setOpenRevokeModal(true);
  };

  const fetchResignationRequests = async (
    page,
    pageSize,
    statusFilter = "",
    empIdFilter = "",
    empStatusFilter = ""
  ) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      let url = `manager/getResignationList?page=${page}&size=${pageSize}`;

      if (statusFilter) {
        switch (statusFilter.toLowerCase()) {
          case "pending":
            url += `&status=1&approved=0&cancelled=0&revoked=0&revokedPermission=0`;
            break;
          case "approved":
            url += `&approved=1&cancel=0`;
            break;
          case "rejected":
            url += `&approved=2`;
            break;
          case "cancelled":
            url += `&cancel=1&approved=1`;
            break;
          default:
            break;
        }
      }
      if (empIdFilter) {
        url += `&empId=${empIdFilter}`;
      }
      if (empStatusFilter !== "") {
        url += `&empStatus=${empStatusFilter}`;
      }

      const response = await GET(url);

      if (
        response.status === 200 &&
        response.data &&
        Array.isArray(response?.data?.data?.paginatedData)
      ) {
        const data = response.data.data.paginatedData;
        const totalRecords = response.data.data.totalItems || 0;
        const totalPages = response.data.data.totalPages || 0;

        if (data.length === 0) {
          setSnackMessage("No resignation requests found.");
          setSnackColor("warning");
          setSnackOpen(true);
        }

        const formattedData = data.map((item, index) => {
          const status = parseInt(item.status);
          let statusLabel = "";
          if (status === 1 && item.approved === 0 && item.revokedPermission === 0) {
            statusLabel = "Pending";
          } else if (item.approved === 1 && item.revokedPermission === 0 && item.cancel === 0) {
            statusLabel = "Approved";
          } else if (item.cancel === 1 && item.approved === 1) {
            statusLabel = "Cancelled";
          } else if (status === 0 && item.approved === 2) {
            statusLabel = "Rejected";
          } else if (
            status === 1 &&
            item.approved === 1 &&
            item.revokedPermission === 1 &&
            item.revoked === 0
          ) {
            statusLabel = "Revoke Granted";
          } else if (status === 1 && item.revoked === 0 && item.revokedPermission === 1) {
            statusLabel = "Revoke Granted";
          } else if (
            status === 0 &&
            item.revoked === 1 &&
            item.approved === 0 &&
            item.revokedPermission === 1
          ) {
            statusLabel = "Revoked";
          } else if (
            status === 0 &&
            item.revoked === 1 &&
            item.approved === 1 &&
            item.revokedPermission === 1
          ) {
            statusLabel = "Revoked";
          }

          let resignationNoticeFormatted = "N/A";
          if (item.resignationNotice && item.resignationNotice > 0) {
            resignationNoticeFormatted = `${item.resignationNotice} Month${
              item.resignationNotice > 1 ? "s" : ""
            }`;
          }

          return {
            id: index + 1 + page * pageSize,
            empId: item.empId,
            empName: `${item.firstName} ${item.lastName}`,
            resignationNotice: resignationNoticeFormatted,
            applyDate: dayjs(item.applyDate).format("DD MMM YYYY"),
            endDate: dayjs(item.endDate).format("DD MMM YYYY"),
            reason: item.reason || "No reason provided",
            status: statusLabel,
            resignationRequestId: item.resignRequestId,
            joinDate: dayjs(item.joinDate).format("DD MMM YYYY"),
            changeNoticePeriodReason: item.changeNoticePeriodReason || "",
            changedBy: item?.endDateChangedBy || "",
            proRatedUpl: Math.floor(item?.proRatedUpl || 0),
            encashedUpl: Math.floor(item?.encashedUpl || 0),
            remainingUplBalance: Math.floor(item?.remainingUplBalance || 0),
            finalEncashableUpl: Math.floor(item?.finalEncashableUpl || 0),
            uplUtilizedInResignationYear: Math.floor(item?.uplUtilizedInResignationYear || 0),
            handleApprove,
            handleDenied,
            handleRevokeResignation,
            handleEdit,
            handleView: handleViewDetails
          };
        });

        setResignationRequests(formattedData);
        setFilteredResignationRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(totalPages);
      }
    } catch (error) {
      console.error("Error fetching resignation requests:", error);
      setSnackMessage(error.response?.data?.message || "Failed to fetch resignation requests.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchResignationRequests(
      paginationModel.page,
      paginationModel.pageSize,
      appliedStatusFilter,
      appliedEmpIdFilter,
      appliedEmpStatusFilter
    );
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    appliedStatusFilter,
    appliedEmpIdFilter,
    appliedEmpStatusFilter
  ]);

  const handleSearchClick = () => {
    setAppliedStatusFilter(statusFilter);
    setAppliedEmpIdFilter(empIdFilter);
    setAppliedEmpStatusFilter(empStatusFilter);
    setPaginationModel({page: 0, pageSize: paginationModel.pageSize});
  };

  // Updated pagination handlers
  const handlePageChange = newPage => {
    const updatedPaginationModel = {...paginationModel, page: newPage};
    setPaginationModel(updatedPaginationModel);
    fetchResignationRequests(
      newPage,
      paginationModel.pageSize,
      appliedStatusFilter,
      appliedEmpIdFilter,
      appliedEmpStatusFilter
    );
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const updatedPaginationModel = {...paginationModel, page: lastPageIndex};
      setPaginationModel(updatedPaginationModel);
      await fetchResignationRequests(
        lastPageIndex,
        paginationModel.pageSize,
        appliedStatusFilter,
        appliedEmpIdFilter,
        appliedEmpStatusFilter
      );
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  const handleRequestApproval = async e => {
    if (!reason) {
      setSnackMessage("Please provide a reason.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }
    const resignationRequestIds = [selectedResignationRequest.resignationRequestId];
    const empId = [selectedResignationRequest.empId];
    const reasonText = reason;
    setOpenApprovalModal(false);
    try {
      let url = "";
      if (action === "approve") {
        url = `manager/approveResignation?resign_id=${resignationRequestIds[0]}&emp_id=${empId}&reason=${reasonText}`;
      } else if (action === "reject") {
        url = `manager/deniedResignation?resign_id=${resignationRequestIds[0]}&emp_id=${empId}&reason=${reasonText}`;
      }
      const response = await POST(url);
      if (response.status === 200) {
        setSnackMessage("Request processed successfully.");
        setSnackColor("success");
        setSnackOpen(true);
        setResignationRequests(prev =>
          prev.map(req =>
            resignationRequestIds.includes(req.resignationRequestId)
              ? {...req, status: action === "approve" ? "Approved" : "Rejected"}
              : req
          )
        );
        setFilteredResignationRequests(prev =>
          prev.map(req =>
            resignationRequestIds.includes(req.resignationRequestId)
              ? {...req, status: action === "approve" ? "Approved" : "Rejected"}
              : req
          )
        );
        refetchCurrentPage();
      } else {
        setSnackMessage("Something went wrong.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage("An error occurred while processing the resignation request.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const handleFormSubmit = async e => {
    e.preventDefault();

    setEndDateError("");
    setChangeNoticePeriodReasonError("");

    let hasError = false;

    if (!endDate) {
      setEndDateError("New End Date is required.");
      hasError = true;
    } else {
      const today = dayjs().startOf("day");
      const selectedEndDate = dayjs(endDate);
      if (selectedEndDate.isBefore(today, "day")) {
        setEndDateError("End date must be in the future.");
        hasError = true;
      }
    }

    if (!changeNoticePeriodReason || changeNoticePeriodReason.trim() === "") {
      setChangeNoticePeriodReasonError("Reason for changing the notice period is required.");
      hasError = true;
    }

    if (hasError) {
      return;
    }
    try {
      const url = `manager/resignation/changeNoticePeriod/${selectedResignationRequest.resignationRequestId}`;
      const payload = {
        endDate: endDate,
        changedBy: changedBy,
        changeNoticePeriodReason: changeNoticePeriodReason
      };
      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage("Notice period updated successfully!");
        setSnackColor("success");
        setSnackOpen(true);
        setResignationRequests(prev =>
          prev.map(request =>
            request.resignationRequestId === selectedResignationRequest.resignationRequestId
              ? {...request, endDate}
              : request
          )
        );
        setFilteredResignationRequests(prev =>
          prev.map(request =>
            request.resignationRequestId === selectedResignationRequest.resignationRequestId
              ? {...request, endDate}
              : request
          )
        );
        setOpenEditModal(false);
        refetchCurrentPage();
      } else {
        setSnackMessage("Something went wrong.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage("An error occurred while updating the notice period.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const handleRevokeAction = async () => {
    const empId = Number(window.sessionStorage.getItem("empId"));
    const resignationRequestId = selectedResignationRequest.resignationRequestId;
    const revokedPermissionDate = dayjs().format("YYYY-MM-DD");
    const requestBody = {
      revokedPermissionDate: revokedPermissionDate,
      revokedPermission: 1
    };
    try {
      const url = `manager/resignation/revokedPermission/${resignationRequestId}?empId=${empId}`;
      const response = await POST(url, requestBody);
      if (response.status === 200) {
        setSnackMessage(
          response?.data?.message || "Resignation revoked permission assigned successfully."
        );
        setSnackColor("success");
        setSnackOpen(true);
        setResignationRequests(prev =>
          prev.map(req =>
            req.resignationRequestId === resignationRequestId
              ? {...req, status: "Revoke Granted"}
              : req
          )
        );
        setFilteredResignationRequests(prev =>
          prev.map(req =>
            req.resignationRequestId === resignationRequestId
              ? {...req, status: "Revoke Granted"}
              : req
          )
        );
        setOpenRevokeModal(false);
        refetchCurrentPage();
      } else {
        setSnackMessage("Failed to revoke resignation.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage("Error occurred while revoking resignation.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const refetchCurrentPage = () => {
    setIsFetching(true);
    fetchResignationRequests(
      paginationModel.page,
      paginationModel.pageSize,
      appliedStatusFilter
    ).finally(() => setIsFetching(false));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Resignation Request List
                </Typography>
                <MDBox sx={{gridColumn: "span 2"}}>
                  <TextField
                    label="Employee ID"
                    value={empIdFilter}
                    onChange={e => setEmpIdFilter(e.target.value)}
                    fullWidth
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                    variant="outlined"
                    sx={{height: "45px"}}
                  />
                </MDBox>
                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={statusFilter}
                    label="Status"
                    name="status"
                    onChange={e => setStatusFilter(e.target.value)}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="emp-status-select-label">Employee Status</InputLabel>
                  <Select
                    labelId="emp-status-select-label"
                    value={empStatusFilter}
                    label="Employee Status"
                    name="empStatus"
                    onChange={e => setEmpStatusFilter(e.target.value)}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearchClick}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredResignationRequests}
              columns={columns}
              paginationMode="server"
              rowCount={totalItems}
              pageSizeOptions={[20]}
              paginationModel={paginationModel}
              loading={isFetching}
              size="small"
              hideFooter
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                fontFamily: properties.fontFamily,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
              aria-label="Total Resignation Requests"
            />
            <CustomPagination
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              totalRows={totalItems}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={isFetching}
            />
          </Paper>
        </MDBox>
        <Snackbar
          open={snackOpen}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snackColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          sx={{"& .MuiDialog-paper": {width: "700px", maxWidth: "none"}}}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Resignation Request Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedResignationRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Joining Date"
                      value={selectedResignationRequest.joinDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Applied Date"
                      value={selectedResignationRequest.applyDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Relieving Date"
                      value={selectedResignationRequest.endDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Resignation Notice"
                      value={selectedResignationRequest.resignationNotice || "No Notice Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedResignationRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedResignationRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openApprovalModal}
          onClose={() => setOpenApprovalModal(false)}
          fullWidth
          sx={{"& .MuiDialog-paper": {width: "700px", maxWidth: "none"}}}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              {action === "approve" ? "Approve Resignation Request" : "Reject Resignation Request"}
            </MDTypography>
          </DialogTitle>
          <DialogContent
            sx={{padding: 2}}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (reason.trim().length > 0) {
                  handleRequestApproval();
                }
              }
            }}
          >
            {selectedResignationRequest && (
              <form>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      label="Joining Date"
                      value={selectedResignationRequest?.joinDate || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest?.empName || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      label="Applied On"
                      value={selectedResignationRequest?.applyDate || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Resignation Notice"
                      value={selectedResignationRequest?.resignationNotice || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Status"
                      value={selectedResignationRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Employee Reason"
                      value={selectedResignationRequest.reason || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Reason<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={reason}
                      onChange={e => {
                        setReason(e.target.value);
                        setIsReasonProvided(e.target.value.trim().length > 0);
                      }}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      multiline
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={() => setOpenApprovalModal(false)}
              variant="contained"
            >
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              onClick={handleRequestApproval}
              variant="contained"
              disabled={!isReasonProvided}
            >
              Submit
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          fullWidth
          sx={{"& .MuiDialog-paper": {width: "700px", maxWidth: "none"}}}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Edit Resignation Notice Period
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedResignationRequest && (
              <form onSubmit={handleFormSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Joining Date"
                      value={selectedResignationRequest.joinDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      label="Applied Date"
                      value={selectedResignationRequest.applyDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Official Notice Period"
                      value={selectedResignationRequest.resignationNotice || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Status"
                      value={selectedResignationRequest.status || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={
                        <span>
                          New End Date<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      type="date"
                      error={!!endDateError}
                      helperText={endDateError}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={
                        <span>
                          Changed By<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={changedBy}
                      onChange={e => setChangedBy(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Reason for Change<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={changeNoticePeriodReason}
                      onChange={e => {
                        setChangeNoticePeriodReason(e.target.value);
                        setChangeNoticePeriodReasonError("");
                      }}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      helperText={
                        changeNoticePeriodReasonError ||
                        "Provide a reason for changing the notice period"
                      }
                    />
                  </Grid>
                </Grid>
                <DialogActions style={{justifyContent: "flex-end"}}>
                  <MDButton
                    color="secondary"
                    onClick={() => setOpenEditModal(false)}
                    variant="contained"
                  >
                    Close
                  </MDButton>
                  <MDButton
                    color="info"
                    variant="contained"
                    type="submit"
                    disabled={
                      !endDate ||
                      !!endDateError ||
                      !changeNoticePeriodReason?.trim() ||
                      !!changeNoticePeriodReasonError
                    }
                  >
                    Submit Changes
                  </MDButton>
                </DialogActions>
              </form>
            )}
          </DialogContent>
        </Dialog>
        <Dialog
          open={openRevokeModal}
          onClose={() => setOpenRevokeModal(false)}
          fullWidth
          sx={{"& .MuiDialog-paper": {width: "700px", maxWidth: "none"}}}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Grant Revoke Permission
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedResignationRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Joining Date"
                      value={selectedResignationRequest.joinDate || "No Join Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee ID"
                      value={selectedResignationRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Employee Name"
                      value={selectedResignationRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Applied Date"
                      value={selectedResignationRequest.applyDate || "No Date Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Resignation Notice"
                      value={selectedResignationRequest.resignationNotice || "N/A"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Status"
                      value={selectedResignationRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedResignationRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={
                        <span>
                          Revoke Date<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      type="date"
                      value={revokeDate}
                      onChange={e => setRevokeDate(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                      InputLabelProps={{shrink: true}}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={() => setOpenRevokeModal(false)}
              variant="contained"
            >
              Close
            </MDButton>
            <MDButton color="error" variant="contained" onClick={handleRevokeAction}>
              Revoke Resignation
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default ResignationRequestList;
